import { Card, DataTable, TextField, Text, BlockStack } from "@shopify/polaris";
import { useCallback } from "react";

const CATEGORIES = [
  "Dry",
  "Canned",
  "Treats",
  "Food Refrigerated",
  "Food Frozen",
  "Litter",
  "All",
] as const;

export interface CategoryMinLevel {
  category: string;
  minWeeksSupply: number;
}

interface Props {
  levels: CategoryMinLevel[];
  onChange: (levels: CategoryMinLevel[]) => void;
}

export default function CategoryMinLevelsEditor({ levels, onChange }: Props) {
  const getLevelValue = useCallback(
    (category: string): string => {
      const found = levels.find((l) => l.category === category);
      return found ? String(found.minWeeksSupply) : "";
    },
    [levels]
  );

  const handleChange = useCallback(
    (category: string, value: string) => {
      const numValue = parseFloat(value);
      const filtered = levels.filter((l) => l.category !== category);

      if (value && !isNaN(numValue) && numValue > 0) {
        filtered.push({ category, minWeeksSupply: numValue });
      }

      onChange(filtered);
    },
    [levels, onChange]
  );

  const rows = CATEGORIES.map((category) => [
    <Text as="span" variant="bodyMd" key={`label-${category}`}>
      {category}
    </Text>,
    <TextField
      key={`input-${category}`}
      label=""
      labelHidden
      type="number"
      value={getLevelValue(category)}
      onChange={(val) => handleChange(category, val)}
      min={0}
      step={0.5}
      suffix="weeks"
      autoComplete="off"
    />,
  ]);

  return (
    <Card>
      <BlockStack gap="400">
        <Text variant="headingMd" as="h2">
          Category Minimum Levels (Weeks Supply)
        </Text>
        <Text as="p" tone="subdued">
          Set minimum weeks of supply to maintain for each product category.
          Leave blank for categories with no minimum.
        </Text>
        <DataTable
          columnContentTypes={["text", "text"]}
          headings={["Category", "Min Weeks Supply"]}
          rows={rows}
        />
      </BlockStack>
    </Card>
  );
}
